import type Phaser from "phaser";
import { CharacterPlayback, STANDING_ANIMS, flightShadowWidth, frameIndex, validateSheet, type Facing, type SheetAnim, type SheetDescription } from "odyssey-shared";
import { arrangeLayers, layerRank, sharedStrips, type LayerPlace } from "./character-layers.ts";
import { StripChoice } from "./character-strip.ts";
import { JumpShadow } from "./jump/shadow.ts";

/**
 * Layered character sprites (ADR-009, ADR-036): one Phaser sprite per layer inside a container, every
 * layer showing the same animation, direction and frame, origins from each sheet's pivot so the container
 * position is where the feet stand, and depth from the feet row so characters sort among themselves and
 * with props.
 */

/**
 * The character manifest the build writes at `assets/characters/current.json`.
 */
export interface CharacterManifest {
  readonly characters: Readonly<Record<string, SheetDescription>>;
}

/**
 * Parses and validates a character manifest. A sheet needs an idle strip and no more, so a standing NPC's look
 * (ADR-136 decision 7.1) is in the manifest; a figure that moves asks for `walk` only when it moves, and the layered
 * sprite skips a layer whose sheet lacks the strip.
 *
 * @param value Parsed JSON.
 * @returns The manifest.
 * @throws Error when the shape is wrong or a sheet is malformed.
 */
export function parseCharacterManifest(value: unknown): CharacterManifest {
  if (typeof value !== "object" || value === null || typeof (value as { characters?: unknown }).characters !== "object") {
    throw new Error("character manifest must hold a characters object");
  }
  const characters: Record<string, SheetDescription> = {};
  for (const [name, sheet] of Object.entries((value as { characters: Record<string, unknown> }).characters)) {
    characters[name] = validateSheet(sheet, STANDING_ANIMS);
  }
  return { characters };
}

/**
 * The texture key of one strip.
 *
 * @param character Character name.
 * @param anim Animation name.
 * @param direction Direction key.
 * @returns The key.
 */
export function stripKey(character: string, anim: string, direction: string): string {
  return `character:${character}:${anim}:${direction}`;
}

/**
 * No characters, for a caller that skips none.
 */
const NO_CHARACTERS: ReadonlySet<string> = new Set();

/**
 * One strip of a character sheet as a spritesheet to load: its texture key, its URL and its frame size.
 */
export interface CharacterStrip {
  readonly key: string;
  readonly url: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
}

/**
 * Every strip of every character in a manifest that the game draws from, which the scene loads as spritesheets.
 *
 * @param manifest The manifest.
 * @param base URL prefix the manifest's `assets/...` paths are relative to.
 * @param skip Characters the page never draws from, by name, whose strips are left out; none unless given.
 * @returns The strips, by character, animation and direction in the manifest's order.
 */
export function characterStrips(manifest: CharacterManifest, base: string, skip: ReadonlySet<string> = NO_CHARACTERS): CharacterStrip[] {
  const strips: CharacterStrip[] = [];
  for (const [name, sheet] of Object.entries(manifest.characters)) {
    if (skip.has(name)) {
      continue;
    }
    for (const [animName, anim] of Object.entries(sheet.anims)) {
      for (const [direction, file] of Object.entries(anim.files)) {
        strips.push({ key: stripKey(name, animName, direction), url: `${base}/${file}`, frameWidth: sheet.frameWidth, frameHeight: sheet.frameHeight });
      }
    }
  }
  return strips;
}

/**
 * What a character shows this frame: where, which way it faces, whether it moved since the last frame, and whether
 * it runs (ADR-116 decision 14), which picks the run strip while it moves. A pose that names no `running` is not
 * running.
 */
export interface Pose {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  readonly moving: boolean;
  readonly running?: boolean;
}

/**
 * A character or creature on screen, however it is drawn: what the scene, the session, the silhouette and the world
 * UI ask of one. The layered `CharacterSprite` is one; a composed look (`looks/look-sprite.ts`, ADR-094 decision 14)
 * is another.
 */
export interface CharacterFigure {
  /** The strips the figure plays beyond idle and walk. */
  readonly playback: CharacterPlayback;
  /** Whether its weapon is drawn, which picks the drawn variants of its idle and walk strips where it has them. */
  drawn: boolean;
  /**
   * Places the figure and picks its frame and direction.
   *
   * @param pose Position in pixels (feet), facing and motion.
   * @param nowMs Scene time in ms.
   */
  setPose(pose: Pose, nowMs: number): void;
  /** The texture keys it draws from now, bottom first, for tests and screenshots. */
  readonly stack: string[];
  /** The frame it shows now, its place in its strip. */
  readonly frame: number;
  /** The depth it draws at now. */
  readonly depth: number;
  /**
   * The name of the strip it shows now, for tests and the HUD; empty with nothing to draw. A name, not always a
   * texture: the layered sprite's is its bottom layer's texture key, which the silhouette draws its copy from
   * (`silhouette.ts`); a composed look's is `look:<strip>:<direction>`, which names no texture.
   */
  readonly strip: string;
  /**
   * The texture and the frame its shape is read from, for a copy drawn over it (`silhouette.ts`); an empty key with
   * nothing to draw. The layered sprite's are its bottom layer's strip and frame; a composed look's are the page and
   * the frame its one sprite shows, so its shape is the character as drawn, with what it wears and holds (ADR-094's
   * amendment of 2026-10-07).
   */
  readonly shapeKey: string;
  readonly shapeFrame: number;
  /** Where its feet stand now, in world pixels: its true feet, which a hop's lift does not move. */
  readonly x: number;
  readonly y: number;
  /**
   * How far above its feet it is drawn now, in whole world pixels: the hop's arc while its `jump` strip plays (ADR-116's
   * third amendment), 0 otherwise. Its art, and a copy drawn over it, stand at `y - lift`; its depth stays its feet's.
   */
  readonly lift: number;
  /** Its origin across and down its frame, for a copy drawn over it. */
  readonly originX: number;
  readonly originY: number;
  /**
   * The frame a strip's strike lands on, as its sheet names it (ADR-095's amendment of 2026-10-10), for the attack's
   * sound.
   *
   * @param strip The strip's name.
   * @returns The impact frame, or null for a strip whose sheet names none.
   */
  impactOf(strip: string): number | null;
  /**
   * Sets its opacity, 0 to 1, over everything it draws.
   *
   * @param alpha The opacity.
   */
  setAlpha(alpha: number): void;
  /** Removes it from the scene. */
  destroy(): void;
}

/**
 * One character on screen. Idle and walk come from its motion (ADR-036 decision 3); a strip beyond them (a swing, a
 * hurt, the death held) comes from its playback (ADR-036's amendment for P2.1-T4), which the page's own character
 * drives from intent and its own events and every other character from its animation bits. Every layer shows the same
 * strip and frame, the bottom layer's sheet deciding the strip's length. A swing's strip (one an ability names) keeps
 * the direction of its first frame for its whole length, whatever the facing does meanwhile (ADR-073's amendment "the
 * way a displacement goes, and a swing keeps its facing"): the state's hold counts inputs, so a watcher's strip, which
 * starts when the bits arrive, could otherwise end on the walk's facing.
 *
 * Layers draw in the order they are named unless they are placed (`character-layers.ts`): a placed layer draws at its
 * rank, which a table may give frame by frame, so a weapon goes behind the body in one frame and in front of it in the
 * next. While `drawn` is set, motion plays the sheet's `idle_drawn` and `walk_drawn` strips when it has them. A
 * character plays only the strips every one of its layers has (`sharedStrips`), so a layer with walking art alone
 * keeps the whole character to its motion.
 *
 * While its `jump` strip plays, the container is drawn `lift` pixels above the feet and a ground shadow is drawn on
 * the feet line (`jump/shadow.ts`); the depth stays the feet's (ADR-116's third amendment).
 */
export class CharacterSprite implements CharacterFigure {
  private readonly container: Phaser.GameObjects.Container;
  private readonly layers: { readonly name: string; readonly sheet: SheetDescription; readonly sprite: Phaser.GameObjects.Sprite; readonly place: LayerPlace }[] = [];
  private readonly ranks: number[] = [];
  private readonly arrangement: number[] = [];
  private readonly placed: boolean;
  private readonly strips: Readonly<Record<string, SheetAnim>>;
  private readonly choice = new StripChoice();
  private readonly shadow: JumpShadow;
  private feetX = 0;
  private feetY = 0;
  private raised = 0;
  private scale = 1;

  /**
   * The strips the character plays beyond idle and walk.
   */
  public readonly playback = new CharacterPlayback();

  /**
   * Whether the character's weapon is drawn, which picks the drawn variants of its idle and walk strips on a sheet that
   * has them. Only the page's own character is ever told: a stance is the page's intent alone.
   */
  public drawn = false;

  /**
   * @param scene The scene.
   * @param manifest Loaded and queued manifest.
   * @param layerNames Character names to stack, bottom first.
   * @param depthOf Depth for a feet position in pixels, x then y, so the caller decides how characters sort with props.
   * @param places Where each layer stacks, by its index in `layerNames`; without it the layers stack as named.
   * @throws Error when a layer names a character the manifest lacks.
   */
  public constructor(scene: Phaser.Scene, manifest: CharacterManifest, layerNames: readonly string[], private readonly depthOf: (feetX: number, feetY: number) => number, places?: readonly LayerPlace[]) {
    this.container = scene.add.container(0, 0);
    this.placed = places !== undefined;
    for (let index = 0; index < layerNames.length; index += 1) {
      const name = layerNames[index] as string;
      const sheet = manifest.characters[name];
      if (sheet === undefined) {
        throw new Error(`character manifest has no ${name}`);
      }
      const sprite = scene.add.sprite(0, 0, stripKey(name, this.choice.anim, this.choice.direction), 0).setOrigin(sheet.pivot.x / sheet.frameWidth, sheet.pivot.y / sheet.frameHeight);
      this.container.add(sprite);
      const place = places?.[index] ?? { rank: index, order: null };
      this.layers.push({ name, sheet, sprite, place });
      this.ranks.push(place.rank);
      this.arrangement.push(index);
    }
    this.strips = sharedStrips(this.layers.map((layer) => layer.sheet.anims));
    this.shadow = new JumpShadow(scene, this.container);
  }

  /**
   * Draws the character at a size other than its sheet's, about its feet: an NPC's look scale (ADR-136 decision 2.4).
   *
   * @param scale The factor, an exact half or double so the art's pixels stay whole.
   * @returns This figure.
   */
  public setDrawScale(scale: number): this {
    this.container.setScale(scale);
    this.scale = scale;
    return this;
  }

  /**
   * Places the character and picks its frame and direction: the pose's facing, except during a swing's strip, which
   * keeps the direction its first frame was drawn in. A hop's lift raises the container and shows the shadow; the
   * depth is the feet's.
   *
   * @param pose Position in pixels (feet), facing and motion.
   * @param nowMs Scene time in ms.
   */
  public setPose(pose: Pose, nowMs: number): void {
    const choice = this.choice;
    const bottom = this.layers[0];
    choice.choose(bottom === undefined ? null : this.strips, this.playback, pose.facing, pose.moving, pose.running === true, this.drawn, nowMs);
    const anim = choice.anim;
    const direction = choice.direction;
    const depth = this.depthOf(pose.x, pose.y);
    this.feetX = pose.x;
    this.feetY = pose.y;
    this.raised = Math.round(choice.altitude * this.scale);
    this.container.setPosition(pose.x, pose.y - choice.lift - this.raised);
    this.container.setDepth(depth);
    if (choice.height >= 0) {
      this.shadow.placeWidth(pose.x, pose.y, choice.height > 0 ? flightShadowWidth(choice.height, this.scale) : 0, depth);
    } else {
      this.shadow.place(pose.x, pose.y, choice.lift, depth);
    }
    for (let index = 0; index < this.layers.length; index += 1) {
      const layer = this.layers[index] as (typeof this.layers)[number];
      const sheetAnim = layer.sheet.anims[anim];
      if (sheetAnim === undefined) {
        this.ranks[index] = layer.place.rank;
        continue;
      }
      const frame = choice.playing ? Math.min(this.playback.frame, sheetAnim.frames - 1) : frameIndex(sheetAnim, nowMs - choice.startMs);
      layer.sprite.setTexture(stripKey(layer.name, anim, direction), frame);
      this.ranks[index] = layerRank(layer.place, anim, direction, frame);
    }
    if (this.placed && arrangeLayers(this.ranks, this.layers.length, this.arrangement)) {
      this.restack();
    }
  }

  /**
   * Puts the layers' sprites in the container in the arrangement's order, bottom first.
   */
  private restack(): void {
    for (let position = 0; position < this.arrangement.length; position += 1) {
      const layer = this.layers[this.arrangement[position] as number];
      if (layer !== undefined) {
        this.container.moveTo(layer.sprite, position);
      }
    }
  }

  /**
   * The layers' texture keys in the order they draw now, bottom first, for tests and screenshots.
   *
   * @returns The strip keys.
   */
  public get stack(): string[] {
    return this.arrangement.map((index) => this.layers[index]?.sprite.texture.key ?? "");
  }

  /**
   * The frame the bottom layer shows now, for tests.
   *
   * @returns The frame index within its strip.
   */
  public get frame(): number {
    return this.choice.frame;
  }

  /**
   * The depth the character draws at now.
   *
   * @returns The container depth.
   */
  public get depth(): number {
    return this.container.depth;
  }

  /**
   * The frame a strip's strike lands on, as the bottom layer's sheet names it.
   *
   * @param strip The strip's name.
   * @returns The impact frame, or null when the sheet names none or lacks the strip.
   */
  public impactOf(strip: string): number | null {
    return this.layers[0]?.sheet.anims[strip]?.impact ?? null;
  }

  /**
   * The texture key the bottom layer currently shows, for tests and the HUD.
   *
   * @returns The strip key, or an empty string with no layers.
   */
  public get strip(): string {
    return this.layers[0]?.sprite.texture.key ?? "";
  }

  /**
   * The texture the character's shape is read from: the strip its bottom layer shows.
   *
   * @returns The strip key, or an empty string with no layers.
   */
  public get shapeKey(): string {
    return this.strip;
  }

  /**
   * The frame of `shapeKey` the bottom layer shows now.
   *
   * @returns The frame index within its strip.
   */
  public get shapeFrame(): number {
    return this.choice.frame;
  }

  /**
   * Where the feet stand now, in world pixels, x.
   *
   * @returns The x it was last posed at.
   */
  public get x(): number {
    return this.feetX;
  }

  /**
   * Where the feet stand now, in world pixels, y: the true feet, below the container while a hop lifts it.
   *
   * @returns The y it was last posed at.
   */
  public get y(): number {
    return this.feetY;
  }

  /**
   * How far above its feet the character is drawn now.
   *
   * @returns The hop's lift or the flight's altitude in whole world pixels, 0 on the ground.
   */
  public get lift(): number {
    return this.choice.lift + this.raised;
  }

  /**
   * The bottom layer's origin across its frame, from its sheet's pivot, for a copy drawn over it.
   *
   * @returns The origin in `[0, 1]`, or 0 with no layers.
   */
  public get originX(): number {
    return this.layers[0]?.sprite.originX ?? 0;
  }

  /**
   * The bottom layer's origin down its frame, from its sheet's pivot, for a copy drawn over it.
   *
   * @returns The origin in `[0, 1]`, or 0 with no layers.
   */
  public get originY(): number {
    return this.layers[0]?.sprite.originY ?? 0;
  }

  /**
   * Sets the opacity of the character's layers.
   *
   * @param alpha The opacity, 0 to 1.
   */
  public setAlpha(alpha: number): void {
    this.container.setAlpha(alpha);
  }

  /**
   * Removes the character from the scene.
   */
  public destroy(): void {
    this.shadow.destroy();
    this.container.destroy(true);
  }
}
